import { Button } from '@/components/ui';
import { BalticChart } from './BalticChart';
import type { BalticRoute, BalticHistoryRequest, BalticHistoryPoint } from '@/types/baltic.types';
import { formatBalticDate } from '@/utils/dateUtils';
import {
  formatBalticValue,
  formatBalticChange,
  formatTCEValue,
  getChangeColorClass,
  getBalticRouteDate,
  hasTIAssessment,
} from '@/utils/balticUtils';

interface BalticAccordionItemProps {
  route: BalticRoute;
  isExpanded: boolean;
  showTCE: boolean;
  selectedPeriod: BalticHistoryRequest['period'];
  historyData: BalticHistoryPoint[];
  historyLoading: boolean;
  onToggle: () => void;
  onPeriodChange: (period: BalticHistoryRequest['period']) => void;
  periods: Array<{ value: string; label: string }>;
}

export function BalticAccordionItem({
  route,
  isExpanded,
  showTCE,
  selectedPeriod,
  historyData,
  historyLoading,
  onToggle,
  onPeriodChange,
  periods,
}: BalticAccordionItemProps) {
  // Some routes (e.g. TD34) have no TI assessment - the API still sends 0 for its values
  const hasTI = hasTIAssessment(route);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all dark:border-gray-700 dark:bg-gray-800">
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:px-5 sm:py-3"
      >
        <div className="flex items-center justify-between gap-3">
          {/* Route Info Grid */}
          <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-5 md:gap-3">
            {/* Route Code */}
            <div>
              <div className="text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                {route.route}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                Route
              </div>
            </div>

            {/* Description */}
            <div className="hidden md:block">
              <div className="truncate text-sm text-gray-900 dark:text-white">
                {route.description}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                Description
              </div>
            </div>

            {/* Date */}
            <div>
              <div className="text-sm font-medium tabular-nums text-gray-900 dark:text-white">
                {formatBalticDate(getBalticRouteDate(route))}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                Date
              </div>
            </div>

            {/* Values based on showTCE */}
            {!showTCE ? (
              <>
                {/* BITR */}
                <div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                      {route.biValue !== undefined
                        ? formatBalticValue(route.biValue, route.decimals)
                        : 'N/A'}
                    </span>
                    {route.biValueChange !== undefined && (
                      <span
                        className={`text-xs font-medium ${getChangeColorClass(route.biValueChange)}`}
                      >
                        {formatBalticChange(route.biValueChange, route.decimals)}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                    BITR
                  </div>
                </div>

                {/* TI */}
                <div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                      {hasTI ? formatBalticValue(route.tiValue, route.decimals) : 'N/A'}
                    </span>
                    {hasTI && route.tiValueChange !== undefined && (
                      <span
                        className={`text-xs font-medium ${getChangeColorClass(route.tiValueChange)}`}
                      >
                        {formatBalticChange(route.tiValueChange, route.decimals)}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                    TI
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* BI TCE */}
                <div>
                  <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                    {route.bitce !== undefined ? formatTCEValue(route.bitce) : 'N/A'}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                    BI TCE
                  </div>
                </div>

                {/* TI Scrubber */}
                <div>
                  <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                    {hasTI && route.titceScrubber !== undefined
                      ? formatTCEValue(route.titceScrubber)
                      : 'N/A'}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                    TI scrubber
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Expand Icon */}
          <div className="flex-shrink-0">
            <svg
              className={`h-5 w-5 text-gray-500 transition-transform dark:text-gray-400 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Mobile Description (shown when collapsed) */}
        {!isExpanded && (
          <div className="mt-1.5 text-xs text-gray-600 dark:text-gray-300 sm:text-sm md:hidden">
            {route.description}
          </div>
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50/50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50 sm:px-5 sm:py-4">
          {/* Mobile: Show Description */}
          <div className="mb-3 md:hidden">
            <div className="text-sm text-gray-900 dark:text-white">{route.description}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
              Description
            </div>
          </div>

          {/* Additional Values (shown when expanded) */}
          {showTCE && (
            <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                  {hasTI && route.titceVlsfo !== undefined
                    ? formatTCEValue(route.titceVlsfo)
                    : 'N/A'}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                  TI VLSFO
                </div>
              </div>
            </div>
          )}

          {/* Period Selector */}
          <div className="mb-3 flex flex-col gap-2 sm:mb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
              Historical trends
            </h3>
            <div className="grid grid-cols-4 gap-0.5 rounded-md bg-gray-200 p-0.5 dark:bg-gray-700 sm:gap-1 sm:rounded-lg sm:p-1">
              {periods.map((period) => (
                <Button
                  key={period.value}
                  size="sm"
                  variant={selectedPeriod === period.value ? 'primary' : 'secondary'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPeriodChange(period.value as BalticHistoryRequest['period']);
                  }}
                  className={`whitespace-nowrap px-2 py-1 text-[11px] sm:text-xs ${
                    selectedPeriod === period.value
                      ? ''
                      : 'bg-transparent hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {period.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Chart */}
          <BalticChart
            key={`${route.route}-${showTCE}`}
            data={historyData}
            loading={historyLoading}
            showTCE={showTCE}
            selectedPeriod={selectedPeriod}
          />
        </div>
      )}
    </div>
  );
}
